// @ts-nocheck -- inherited from lift-teams; pre-existing type issues outside this port's scope
import { MultiSelectCheckboxTag, SingleSelect } from 'lift-ui';
import React, { useEffect, useRef, useMemo } from 'react';

import { AlertFormData, Brand, BrandConfig } from '../types';

import { BrandThresholdTable } from './BrandThresholdTable';
import { PacingThresholdTable } from './PacingThresholdTablev2';
import { TaxonomyThresholdTable } from './TaxonomyThresholdTable';

/* eslint-disable @typescript-eslint/no-unused-vars */
interface AlertDetailsStepProps {
  formData: AlertFormData;
  alertTypes: Array<{
    id: number;
    name: string;
    display_name: string;
    description: string;
    data_source: string;
    created_at: string;
    updated_at: string;
    default_threshold_config?: Array<{
      value: number;
      metric: string;
      operator: '>' | '>=' | '<' | '<=' | '==' | '!=';
      max_value?: string | number;
      period_days?: number;
    }>;
    alert_query_config?: {
      properties: string[];
    };
  }>;
  brands: Brand[];
  selectedBrands: BrandConfig[];
  isEditMode?: boolean;
  pixels: Array<{
    id: number;
    brand_id: number;
    account_id: string;
    pixel_id: string;
    pixel_name: string;
    created_at: string;
    updated_at: string;
  }>;
  getEventsForThreshold: (threshold: import('../types').BrandThreshold) => Array<{
    id: number;
    pixel_id_fk: number;
    event_name: string;
    composite_score: number | null;
    snapshot_date: string;
    created_at: string;
  }>;
  onAlertTypeChange: (displayName: string) => void;
  onAddBrand: (brandName: string) => void;
  onRemoveBrand: (brandId: number) => void;
  onPixelSelection: (brandId: number, thresholdIndex: number, pixelName: string) => Promise<void>;
  onUpdateThreshold: (
    brandId: number,
    thresholdIndex: number,
    field: keyof import('../types').BrandThreshold,
    value: string | number
  ) => void;
  onAddThresholdRow: (brandId: number) => void;
  onRemoveThresholdRow: (brandId: number, thresholdIndex: number) => void;
  onReorderThresholds: (brandId: number, fromIndex: number, toIndex: number) => void;
  onClearAllThresholds: (brandId: number) => void;
  isLoadingEvents?: boolean;
  isLoadingInitialData?: boolean;
  fetchEventsForPixels?: (pixelIds: string[]) => Promise<void>;
  onUpdateLogicalOperator?: (
    brandId: number,
    thresholdIndex: number,
    operator: 'AND' | 'OR' | 'NONE'
  ) => void;
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export const AlertDetailsStep: React.FC<AlertDetailsStepProps> = ({
  formData,
  alertTypes,
  brands,
  selectedBrands,
  pixels,
  getEventsForThreshold,
  onAlertTypeChange,
  onAddBrand,
  onRemoveBrand,
  onPixelSelection,
  onUpdateThreshold,
  onAddThresholdRow,
  onRemoveThresholdRow,
  onReorderThresholds,
  onClearAllThresholds,
  isLoadingEvents,
  isLoadingInitialData,
  fetchEventsForPixels,
  onUpdateLogicalOperator,
  isEditMode = false,
}) => {
  const shouldShowBrandDropdown = () => {
    const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
    // Show brand dropdown if:
    // 1. The alert type has brand_id in alert_query_config.properties (like EMQ)
    // 2. OR if the alert type is "pacing" (which also needs brand selection)
    // 3. OR if the alert type is "taxonomy" (which also needs brand selection)
    const hasBrandIdInConfig =
      selectedAlertType?.alert_query_config?.properties?.includes('brand_id') || false;
    const isPacingTool = selectedAlertType?.name === 'pacing';
    const isTaxonomyTool = selectedAlertType?.name === 'taxonomy';
    return hasBrandIdInConfig || isPacingTool || isTaxonomyTool;
  };

  const handleToolChange = (displayName: string | null | undefined) => {
    // If tool is cleared, remove all selected brands and clear toolName
    if (!displayName || displayName.trim() === '') {
      // Remove all brands
      const brandsToRemove = [...selectedBrands];
      brandsToRemove.forEach(brandConfig => {
        onRemoveBrand(brandConfig.brandId);
      });
      // Clear the toolName in formData
      onAlertTypeChange('');
    } else {
      // Otherwise, handle normal tool change
      onAlertTypeChange(displayName);
    }
  };

  const handleBrandSelectionChange = async (selectedBrandNames: string[]) => {
    const currentBrandNames = selectedBrands.map(bc => bc.brandName);

    // Find brands to remove (in current but not in selected)
    const toRemove = currentBrandNames.filter(name => !selectedBrandNames.includes(name));
    toRemove.forEach(brandName => {
      const brandConfig = selectedBrands.find(bc => bc.brandName === brandName);
      if (brandConfig) {
        onRemoveBrand(brandConfig.brandId);
      }
    });

    // Find brands to add (in selected but not in current)
    // Process additions sequentially to ensure proper state updates
    const toAdd = selectedBrandNames.filter(name => !currentBrandNames.includes(name));
    for (const brandName of toAdd) {
      await onAddBrand(brandName);
    }
  };

  // Ref to track the SingleSelect container
  const selectContainerRef = useRef<HTMLDivElement>(null);

  // Add tooltips to SingleSelect options when dropdown opens
  useEffect(() => {
    const addTooltipsToOptions = () => {
      // Find all option buttons in the dropdown
      const optionButtons = document.querySelectorAll('[role="option"]');

      optionButtons.forEach(button => {
        // Skip if already processed
        if (button.hasAttribute('data-tooltip-added')) {
          return;
        }

        const labelElement = button.querySelector('span');
        if (labelElement) {
          const optionLabel = labelElement.textContent?.trim();
          if (optionLabel) {
            // Find the corresponding alert type description
            const alertType = alertTypes.find(at => at.display_name === optionLabel);
            if (alertType && alertType.description) {
              // Add title attribute for native browser tooltip on hover
              button.setAttribute('title', alertType.description);
              button.setAttribute('data-tooltip-added', 'true');
            }
          }
        }
      });
    };

    // Use MutationObserver to watch for dropdown opening
    const observer = new MutationObserver(mutations => {
      // Check if any added nodes contain the dropdown options
      const hasDropdownOptions = mutations.some(mutation =>
        Array.from(mutation.addedNodes).some(
          node =>
            node instanceof Element &&
            (node.querySelector('[role="listbox"]') || node.getAttribute('role') === 'listbox')
        )
      );

      if (hasDropdownOptions) {
        // Small delay to ensure options are fully rendered
        setTimeout(addTooltipsToOptions, 150);
      }
    });

    // Observe the document body for changes (when dropdown opens/closes)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Also add tooltips when clicking on the select (dropdown opens)
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (selectContainerRef.current?.contains(target)) {
        setTimeout(addTooltipsToOptions, 200);
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      observer.disconnect();
      document.removeEventListener('click', handleClick);
    };
  }, [alertTypes]);

  // Sort options: selected items first (in selection order), then unselected items alphabetically
  const sortedBrandOptions = useMemo(() => {
    const selectedBrandNames = new Set(selectedBrands.map(bc => bc.brandName));
    const selected: Array<{ label: string; value: string }> = [];
    const unselected: Array<{ label: string; value: string }> = [];

    // First, add selected items in their selection order
    selectedBrands.forEach(brandConfig => {
      selected.push({
        label: brandConfig.brandName,
        value: brandConfig.brandName,
      });
    });

    // Then, add unselected items
    brands.forEach(brand => {
      if (!selectedBrandNames.has(brand.name)) {
        unselected.push({
          label: brand.name,
          value: brand.name,
        });
      }
    });

    // Sort unselected items alphabetically
    unselected.sort((a, b) => a.label.localeCompare(b.label));

    // Return selected items first (in their selection order), then unselected items
    return [...selected, ...unselected];
  }, [brands, selectedBrands]);

  return (
    <div
      className="bg-white rounded-lg p-6 space-y-6 border border-black/20"
      style={{ minHeight: 'calc(90vh - 7rem)' }}
    >
      <div className="w-full max-w-md" ref={selectContainerRef}>
        <label className="text-sm font-medium text-gray-700 mb-2 block text-left">
          Select a Tool <span className="text-red-900">*</span>
        </label>
        <SingleSelect
          defaultValue={formData.toolName}
          onChange={handleToolChange}
          options={alertTypes
            .filter(alertType =>
              isEditMode
                ? true
                : alertType.display_name !== 'Test Lab Alert'
            )
            .map(alertType => ({
              label: alertType.display_name,
              value: alertType.display_name,
            }))}
          placeholder="Select a tool"
          className="w-full"
          disabled={isEditMode}
        />
      </div>

      {shouldShowBrandDropdown() && (
        <div className="w-full">
          <div className="w-full max-w-md mb-6">
            <MultiSelectCheckboxTag
              label="Select Brands"
              placeholder="Select brands"
              value={selectedBrands.map(bc => bc.brandName)}
              onChange={handleBrandSelectionChange}
              options={sortedBrandOptions}
              required
              state={isEditMode ? 'disabled' : 'default'}
              containerClassName="w-full"
              showSelectAll={false}
            />
          </div>

          {/* Brand-Specific Thresholds */}
          {selectedBrands.map(brandConfig => {
            // Check the selected tool type to determine which threshold table to render
            const selectedAlertType = alertTypes.find(at => at.display_name === formData.toolName);
            const isPacingTool = selectedAlertType?.name === 'pacing';
            const isTaxonomyTool = selectedAlertType?.name === 'taxonomy';

            if (isPacingTool) {
              return (
                <PacingThresholdTable
                  key={brandConfig.brandId}
                  brandConfig={brandConfig}
                  onUpdateThreshold={onUpdateThreshold}
                  onAddThresholdRow={onAddThresholdRow}
                  onRemoveThresholdRow={onRemoveThresholdRow}
                  onReorderThresholds={onReorderThresholds}
                  onClearAllThresholds={onClearAllThresholds}
                  isLoadingInitialData={isLoadingInitialData}
                  alertTypes={alertTypes}
                  toolName={formData.toolName}
                  onUpdateLogicalOperator={onUpdateLogicalOperator}
                />
              );
            }

            if (isTaxonomyTool) {
              return (
                <TaxonomyThresholdTable
                  key={brandConfig.brandId}
                  brandConfig={brandConfig}
                  onUpdateThreshold={onUpdateThreshold}
                  onAddThresholdRow={onAddThresholdRow}
                  onRemoveThresholdRow={onRemoveThresholdRow}
                  onReorderThresholds={onReorderThresholds}
                  onClearAllThresholds={onClearAllThresholds}
                  isLoadingInitialData={isLoadingInitialData}
                  alertTypes={alertTypes}
                  toolName={formData.toolName}
                  onUpdateLogicalOperator={onUpdateLogicalOperator}
                />
              );
            }

            return (
              <BrandThresholdTable
                key={brandConfig.brandId}
                brandConfig={brandConfig}
                pixels={pixels.filter(pixel => pixel.brand_id === brandConfig.brandId)}
                getEventsForThreshold={getEventsForThreshold}
                onPixelSelection={onPixelSelection}
                onUpdateThreshold={onUpdateThreshold}
                onAddThresholdRow={onAddThresholdRow}
                onRemoveThresholdRow={onRemoveThresholdRow}
                onReorderThresholds={onReorderThresholds}
                onClearAllThresholds={onClearAllThresholds}
                isLoadingEvents={isLoadingEvents}
                isLoadingInitialData={isLoadingInitialData}
                fetchEventsForPixels={fetchEventsForPixels}
                alertTypes={alertTypes}
                toolName={formData.toolName}
                onUpdateLogicalOperator={onUpdateLogicalOperator}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};